import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, AlertTriangle, Plus as PlusIcon, Minus } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const UNITS = ['pcs', 'box', 'kg', 'gm', 'ltr', 'ml', 'mtr', 'set', 'pack', 'roll'];
const blankForm = () => ({ name: '', sku: '', category: '', unit: 'pcs', quantity: 0, reorder_level: 0, unit_price: 0 });

export default function StockPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ delta: 0, reason: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      if (lowOnly) params.set('low_stock', 'true');
      const res = await api.get(`/stock?${params}`);
      setItems(res.data);
    } catch { toast.error('Failed to load stock'); }
    finally { setLoading(false); }
  }, [searchText, lowOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditId(null); setForm(blankForm()); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditId(it._id);
    setForm({
      name: it.name || '', sku: it.sku || '', category: it.category || '',
      unit: it.unit || 'pcs', quantity: it.quantity || 0, reorder_level: it.reorder_level || 0, unit_price: it.unit_price || 0
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editId) { await api.put(`/stock/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/stock', form); toast.success('Stock item created'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete stock item?')) return;
    try { await api.delete(`/stock/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleAdjust = async () => {
    if (!adjustDialog) return;
    if (!adjustForm.delta) { toast.error('Enter adjustment amount'); return; }
    try {
      await api.post(`/stock/${adjustDialog._id}/adjust`, adjustForm);
      toast.success('Stock adjusted'); setAdjustDialog(null); setAdjustForm({ delta: 0, reason: '' }); fetchData();
    } catch { toast.error('Failed'); }
  };

  const totalValue = items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const lowCount = items.filter(i => (i.reorder_level || 0) > 0 && (i.quantity || 0) <= (i.reorder_level || 0)).length;

  return (
    <div data-testid="stock-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Stock Register</h1>
          <p className="text-sm text-[#4F734F] mt-1">Inventory levels, reorder alerts, valuation</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu endpoint="/exports/stock" params={{ ...(searchText ? { search: searchText } : {}) }} filename="stock" testIdPrefix="stock" />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-stock-btn"><Plus size={16} /> New Item</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total SKUs</p><p className="font-heading text-2xl font-light text-[#111B11]">{items.length}</p></div>
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total Value</p><p className="font-heading text-2xl font-light text-[#111B11]">INR {totalValue.toLocaleString('en-IN')}</p></div>
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Low Stock</p><p className={`font-heading text-2xl font-light ${lowCount > 0 ? 'text-red-600' : 'text-green-600'}`}>{lowCount}</p></div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar value={searchText} onChange={setSearchText} placeholder="Search name, SKU, category..." testId="stock-search" className="!flex-none w-full sm:w-80" />
        <label className="flex items-center gap-2 text-sm" data-testid="low-stock-toggle">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} />
          <span>Show only low-stock items</span>
        </label>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-[#1B2A1B] text-white">
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Item</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">SKU</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Category</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Unit</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Quantity</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Reorder</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Unit Price</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Value</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-olive-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-olive-400">No stock items</td></tr> :
            items.map(it => {
              const isLow = (it.reorder_level || 0) > 0 && (it.quantity || 0) <= (it.reorder_level || 0);
              return (
                <tr key={it._id} className={`border-b border-[#E6EDE6] hover:bg-[#F8FAF7] ${isLow ? 'bg-red-50/40' : ''}`} data-testid={`stock-row-${it._id}`}>
                  <td className="py-2 px-3 font-medium flex items-center gap-2">{isLow && <AlertTriangle size={13} className="text-red-500" />}{it.name}</td>
                  <td className="py-2 px-3 font-mono text-xs">{it.sku || '-'}</td>
                  <td className="py-2 px-3 text-xs">{it.category || '-'}</td>
                  <td className="py-2 px-3 text-xs">{it.unit}</td>
                  <td className={`py-2 px-3 text-right font-mono font-medium ${isLow ? 'text-red-600' : ''}`}>{it.quantity}</td>
                  <td className="py-2 px-3 text-right text-xs">{it.reorder_level || '-'}</td>
                  <td className="py-2 px-3 text-right font-mono">{(it.unit_price || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3 text-right font-mono">{((it.quantity || 0) * (it.unit_price || 0)).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-0.5">
                      <button onClick={() => { setAdjustDialog(it); setAdjustForm({ delta: 0, reason: '' }); }} className="p-1 hover:bg-blue-50 rounded-sm" title="Adjust stock" data-testid={`adjust-stock-${it._id}`}><PlusIcon size={11} className="text-blue-600" /><Minus size={11} className="text-blue-600 -mt-1" /></button>
                      <button onClick={() => openEdit(it)} className="p-1 hover:bg-olive-100 rounded-sm" title="Edit" data-testid={`edit-stock-${it._id}`}><Pencil size={13} className="text-olive-500" /></button>
                      {isAdmin && <button onClick={() => handleDelete(it._id)} className="p-1 hover:bg-red-50 rounded-sm" title="Delete"><Trash2 size={13} className="text-red-400" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* New/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Stock Item</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Item Name *</Label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="input-field" data-testid="stock-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">SKU</Label><Input value={form.sku} onChange={(e) => setForm(p => ({ ...p, sku: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Category</Label><Input value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm(p => ({ ...p, unit: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm(p => ({ ...p, quantity: Number(e.target.value) }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm(p => ({ ...p, reorder_level: Number(e.target.value) }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Unit Price (INR)</Label><Input type="number" value={form.unit_price} onChange={(e) => setForm(p => ({ ...p, unit_price: Number(e.target.value) }))} className="input-field" /></div>
            </div>
            <div className="flex gap-2 pt-2"><Button onClick={handleSubmit} className="btn-primary" data-testid="save-stock-btn">{editId ? 'Update' : 'Create'}</Button><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust */}
      <Dialog open={!!adjustDialog} onOpenChange={(o) => !o && setAdjustDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-xl">Adjust Stock</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-[#4F734F]"><strong>{adjustDialog?.name}</strong> — current: {adjustDialog?.quantity} {adjustDialog?.unit}</p>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Change (+ to add, − to remove)</Label><Input type="number" value={adjustForm.delta} onChange={(e) => setAdjustForm(p => ({ ...p, delta: Number(e.target.value) }))} className="input-field" data-testid="adjust-delta" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Reason</Label><Input value={adjustForm.reason} onChange={(e) => setAdjustForm(p => ({ ...p, reason: e.target.value }))} className="input-field" placeholder="e.g. damage, audit, manual entry" /></div>
            <div className="flex gap-2"><Button onClick={handleAdjust} className="btn-primary" data-testid="confirm-adjust-btn">Apply</Button><Button variant="outline" onClick={() => setAdjustDialog(null)}>Cancel</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
